import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "config.js"; // import API base URL

function Settings() {
  const [activeTab, setActiveTab] = useState("personal");
  const [user, setUser] = useState({
    name: "",
    email: "",
    twilioPhoneNumber: "",
    phoneNumbers: [],
  });
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  // Helper: Get token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch user data from backend using the token for authorization
  const fetchUserData = async () => {
    console.log("API_BASE_URL:", API_BASE_URL);
    
    setLoading(true); 

    const storedUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (storedUser && storedUser.id) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/${storedUser.id}`,
          { headers: getAuthHeaders() }
        );
        const userData = response.data.data;
        setUser({
          name: userData.name || "Admin",
          email: userData.email || "",
          twilioPhoneNumber: userData.twilioPhoneNumber || "",
          phoneNumbers: userData.phoneNumbers || [],
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false); // ✅ Hide spinner after fetching data
      }
    }
  };

  // Handle changes in personal details form
  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  // Save personal details using token for authorization
  const handleSaveChanges = async () => {
    const storedUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    if (storedUser && storedUser.id) {
      setIsSaving(true);
      try {
        const updateData = {
          name: user.name,
          email: user.email,
          twilioPhoneNumber: user.twilioPhoneNumber,
        };
        await axios.put(
          `${API_BASE_URL}/users/update/${storedUser.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        await fetchUserData();
        Swal.fire({
          title: "Success!",
          text: "Changes saved successfully",
          icon: "success",
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: "#007bff",
        });
      } catch (error) {
        console.error("Error updating user data:", error);
        Swal.fire({
          title: "Error!",
          text: "Error saving changes",
          icon: "error",
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: "#dc3545",
        });
      }
      setIsSaving(false);
    }
  };

  // Handle changes in password fields
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  // Handle password update using token for authorization
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmNewPassword) {
      //   return Swal.fire("Error", "New passwords do not match!", "error");
      return Swal.fire({
        title: "Error!",
        text: "New passwords do not match!",
        icon: "error",
        confirmButtonColor: "#dc3545",
      });
    }
    const storedUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (!storedUser || !storedUser.id) return;
    setIsSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/users/change-password/${storedUser.id}`,
        {
          oldPassword: passwords.oldPassword,
          newPassword: passwords.newPassword,
        },
        { headers: getAuthHeaders() }
      );
      setPasswords({
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      Swal.fire({
        title: "Success!",
        text: "Password changed successfully",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
        confirmButtonColor: "#007bff",
      });
    } catch (error) {
      console.error("Error changing password:", error);
      //   Swal.fire("Error", "Error changing password", "error");
      Swal.fire({
        title: "Error!",
        text: "Error changing password",
        icon: "error",
        confirmButtonColor: "#dc3545",
      });
    }
    setIsSaving(false);
  };

  return (
    <Container fluid className="p-4">
      <Card>
        <Card.Header>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab
              eventKey="personal"
              title={
                <span className="fs-5 fw-bold text-dark">Personal Details</span>
              }
            />
            <Tab
              eventKey="password"
              title={
                <span className="fs-5 fw-bold text-dark">Change Password</span>
              }
            />
          </Tabs>
        </Card.Header>
        <Card.Body>
          {loading ? ( // ✅ Show spinner while loading
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                {/* <span className="visually-hidden">Loading...</span> */}
              </Spinner>
            </div>
          ) : (
            <>
              {activeTab === "personal" && (
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="text-dark fw-bold">
                          Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Enter your name"
                          value={user.name}
                          onChange={handleUserChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="text-dark fw-bold">
                          Email Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          value={user.email}
                          onChange={handleUserChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="text-dark fw-bold">
                          Assigned Phone Numbers:
                        </Form.Label>
                        {user.phoneNumbers.length > 0 ? (
                          <ul>
                            {user.phoneNumbers.map((phone) => (
                              <li key={phone._id}>{phone.phoneNumber}</li>
                            ))}
                          </ul>
                        ) : (
                          <small className="text-muted ml-2">
                            No phone numbers assigned.
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={12} className="text-end">
                      <Button
                        variant="primary"
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="bg-primary"
                      >
                        {isSaving ? "Saving Changes..." : "Save Changes"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
              {activeTab === "password" && (
                <Form onSubmit={handleChangePassword}>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-dark fw-bold">
                          Old Password <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="oldPassword"
                          placeholder="Enter current password"
                          value={passwords.oldPassword}
                          onChange={handlePasswordChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-dark fw-bold">
                          New Password <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="newPassword"
                          placeholder="Enter new password"
                          value={passwords.newPassword}
                          onChange={handlePasswordChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-dark fw-bold">
                          Confirm Password{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmNewPassword"
                          placeholder="Confirm new password"
                          value={passwords.confirmNewPassword}
                          onChange={handlePasswordChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} className="text-end">
                      <Button
                        variant="primary"
                        className="bg-primary"
                        type="submit"
                        disabled={isSaving}
                      >
                        {isSaving ? "Changing..." : "Change Password"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Settings;
